import { useState, useMemo, useEffect, useCallback } from 'react';
import { useGetOrganizationsQuery } from '@/entities/organizations';
import { type Contact, useGetOrganizationContactsQuery } from '@/entities/contacts';
import { useProjectSiteParams } from '@/shared/lib';

type ModalMode = 'view' | 'edit' | 'create';

interface UseContactSelectorProps {
  organizationId?: string;
  organizationName?: string;
  contactId?: string;
  contactName?: string;
  onOrganizationChange: (id: string | undefined) => void;
  onContactChange: (id: string | undefined) => void;
  disabled?: boolean;
}

export const useContactSelector = ({
  organizationId,
  organizationName,
  contactId,
  contactName,
  onOrganizationChange,
  onContactChange,
  disabled = false
}: UseContactSelectorProps) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [contactModalMode, setContactModalMode] = useState<ModalMode>('create');
  const [isOrgModalOpen, setIsOrgModalOpen] = useState(false);
  const [orgModalMode, setOrgModalMode] = useState<ModalMode>('view');
  const { projectId, siteId } = useProjectSiteParams();

  const { data: orgData, isLoading: isLoadingOrgs } = useGetOrganizationsQuery({
    projectId: projectId as number
  });

  useEffect(() => {
    if (orgData?.organizations && !organizationId && organizationName) {
      const match = orgData.organizations.find((org) => org.name.toLowerCase() === organizationName.toLowerCase());
      if (match) {
        onOrganizationChange(String(match.id));
      }
    }
  }, [orgData, organizationId, organizationName, onOrganizationChange]);

  const { data: contactData, isLoading: isLoadingContacts } = useGetOrganizationContactsQuery(
    {
      orgId: organizationId as string,
      projectId: projectId as number,
      siteId: siteId as number
    },
    { skip: !organizationId || !projectId || !siteId }
  );

  const orgOptions = useMemo(() => {
    const options =
      orgData?.organizations.map((org) => ({
        value: String(org.id),
        label: org.name,
        keywords: org.abbreviation ? [org.abbreviation] : undefined
      })) || [];
    if (organizationId && organizationName && !options.find((o) => o.value === organizationId)) {
      options.push({ value: organizationId, label: organizationName, keywords: undefined });
    }
    return options;
  }, [orgData, organizationId, organizationName]);

  const contactOptions = useMemo(() => {
    const options = contactData?.map((c) => ({ value: String(c.id), label: c.name })) || [];
    if (contactId && contactName && !options.find((o) => o.value === contactId)) {
      options.push({ value: contactId, label: contactName });
    }
    return options;
  }, [contactData, contactId, contactName]);

  const selectedContact = contactData?.find((c) => String(c.id) === contactId);

  const handleContactAdd = useCallback(() => {
    setContactModalMode('create');
    setIsModalOpen(true);
  }, []);

  const handleContactOpen = useCallback(() => {
    setContactModalMode(disabled ? 'view' : 'edit');
    setIsModalOpen(true);
  }, [disabled]);

  const handleContactModalClose = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  const handleContactModalSuccess = useCallback(
    (contact: Contact) => {
      if (contact.orgId) {
        onOrganizationChange(String(contact.orgId));
      }
      onContactChange(String(contact.id));
      setIsModalOpen(false);
    },
    [onOrganizationChange, onContactChange]
  );

  const toggleContactDetails = useCallback(() => {
    setShowDetails((prev) => !prev);
  }, []);

  const handleOrgOpen = useCallback(() => {
    setOrgModalMode(disabled ? 'view' : 'edit');
    setIsOrgModalOpen(true);
  }, [disabled]);

  const handleOrgModalClose = useCallback(() => {
    setIsOrgModalOpen(false);
  }, []);

  const handleOrganizationChange = useCallback(
    (val: string | undefined) => {
      onOrganizationChange(val);
      onContactChange(undefined);
    },
    [onOrganizationChange, onContactChange]
  );

  return {
    // State
    isContactModalOpen: isModalOpen,
    showContactDetails: showDetails,
    isLoadingOrgs,
    isLoadingContacts,
    orgOptions,
    contactOptions,
    selectedContact,
    contactModalMode,
    isOrgModalOpen,
    orgModalMode,

    // Handlers
    handleContactAdd,
    handleContactOpen,
    handleContactModalClose,
    handleContactModalSuccess,
    toggleContactDetails,
    handleOrgOpen,
    handleOrgModalClose,
    handleOrganizationChange
  };
};
